import React from 'react';
import { Input, Select, Textarea } from '../FormField';

export interface AssignmentFormValues {
  orderId: string;
  /** '' for none, 'b:<id>' for a bundle, 'f:<id>' for a single fabric. */
  fabricValue: string;
  quantity: string;
  notes: string;
  status: 'draft' | 'complete';
}

export interface AssignmentFieldErrors { order?: string; quantity?: string }

interface Option { value: string; label: string }

interface AssignmentFieldsProps {
  values: AssignmentFormValues;
  onChange: (next: AssignmentFormValues) => void;
  errors: AssignmentFieldErrors;
  /** Creating: the order is chosen. Editing: it is fixed and only shown. */
  orderOptions: Option[] | null;
  orderLabel: string;
  fabricOptions: Option[];
  /** Nothing can be changed (a viewer, or the order is archived). */
  readOnly: boolean;
  /** Bundle and sets cannot change while any line is packed. */
  packedLock: boolean;
  /** The status is chosen by hand only when there is no checklist to derive it from. */
  manualStatus: boolean;
}

const LOCK_HINT = 'Unpack every line to change the bundle or the number of sets.';

/**
 * The fields of an assignment: order, bundle or fabric, sets, notes, and a
 * hand-set status when there is no checklist. They are the standard FormField
 * components in `form-row`s, so the page reads like every other form page.
 */
const AssignmentFields: React.FC<AssignmentFieldsProps> = ({
  values, onChange, errors, orderOptions, orderLabel, fabricOptions, readOnly, packedLock, manualStatus,
}) => {
  const set = (patch: Partial<AssignmentFormValues>) => onChange({ ...values, ...patch });

  return (
    <>
      <div className="form-row">
        {orderOptions ? (
          <Select
            label="Order" required options={orderOptions} value={values.orderId} placeholder="Choose an order"
            onChange={e => set({ orderId: e.target.value })} disabled={readOnly} error={errors.order}
          />
        ) : (
          <Input label="Order" value={orderLabel} disabled readOnly help="Set when the assignment was created." />
        )}
        <Select
          label="Bundle or fabric" options={[{ value: '', label: 'None' }, ...fabricOptions]} value={values.fabricValue}
          placeholder="Choose a bundle or fabric" onChange={e => set({ fabricValue: e.target.value })}
          disabled={readOnly || packedLock} help={packedLock ? LOCK_HINT : undefined}
        />
      </div>

      <div className="form-row">
        <Input
          label="Sets" required type="text" inputMode="decimal" value={values.quantity}
          disabled={readOnly || packedLock} error={errors.quantity} aria-invalid={errors.quantity ? true : undefined}
          onChange={e => set({ quantity: e.target.value })}
        />
        {manualStatus && (
          <Select
            label="Status" value={values.status} disabled={readOnly}
            options={[{ value: 'draft', label: 'Not packed' }, { value: 'complete', label: 'Packed' }]}
            onChange={e => set({ status: e.target.value === 'complete' ? 'complete' : 'draft' })}
            help="This assignment has no checklist, so you set its status yourself."
          />
        )}
      </div>

      <Textarea label="Notes" rows={4} value={values.notes} disabled={readOnly} onChange={e => set({ notes: e.target.value })} />
    </>
  );
};

export default AssignmentFields;
